import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatDate(timestamp: number | null | undefined): string {
  if (!timestamp) return '—';
  // If timestamp is in seconds, convert to ms
  const ms = timestamp > 1e11 ? timestamp : timestamp * 1000;
  const date = new Date(ms);
  return new Intl.DateTimeFormat('default', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
}

export function formatUtc(timestamp: number | null | undefined): string {
  if (!timestamp) return '—';
  const ms = timestamp > 1e11 ? timestamp : timestamp * 1000;
  return new Date(ms).toISOString();
}

export function formatRelativeTime(
  timestamp: number | null | undefined,
  forcedLocale?: string
): string {
  let locale = forcedLocale;
  if (!locale && typeof localStorage !== 'undefined') {
    try {
      locale = localStorage.getItem('hisob_admin_locale') || 'uz';
    } catch {
      locale = 'uz';
    }
  }
  if (!locale) locale = 'uz';

  if (!timestamp) {
    return locale === 'en' ? 'Never' : locale === 'ru' ? 'Никогда' : 'Hech qachon';
  }
  const ms = timestamp > 1e11 ? timestamp : timestamp * 1000;
  const diffSec = Math.floor((Date.now() - ms) / 1000);

  if (diffSec < 10) {
    return locale === 'en' ? 'Just now' : locale === 'ru' ? 'Только что' : 'Hozirgina';
  }
  if (diffSec < 60) {
    return locale === 'en'
      ? `${diffSec}s ago`
      : locale === 'ru'
      ? `${diffSec} сек назад`
      : `${diffSec} soniya oldin`;
  }
  if (diffSec < 3600) {
    const mins = Math.floor(diffSec / 60);
    return locale === 'en'
      ? `${mins}m ago`
      : locale === 'ru'
      ? `${mins} мин назад`
      : `${mins} daqiqa oldin`;
  }
  if (diffSec < 86400) {
    const hours = Math.floor(diffSec / 3600);
    return locale === 'en'
      ? `${hours}h ago`
      : locale === 'ru'
      ? `${hours} ч назад`
      : `${hours} soat oldin`;
  }
  const days = Math.floor(diffSec / 86400);
  return locale === 'en'
    ? `${days}d ago`
    : locale === 'ru'
    ? `${days} дн назад`
    : `${days} kun oldin`;
}

export function formatTimeRemaining(
  expiresAtSec: number,
  forcedLocale?: string
): string {
  let locale = forcedLocale;
  if (!locale && typeof localStorage !== 'undefined') {
    try {
      locale = localStorage.getItem('hisob_admin_locale') || 'uz';
    } catch {
      locale = 'uz';
    }
  }
  if (!locale) locale = 'uz';

  const nowSec = Math.floor(Date.now() / 1000);
  const diffSec = expiresAtSec - nowSec;
  if (diffSec <= 0) {
    return locale === 'en' ? 'Expired' : locale === 'ru' ? 'Истек' : 'Muddati tugagan';
  }

  const hours = Math.floor(diffSec / 3600);
  const minutes = Math.floor((diffSec % 3600) / 60);
  if (hours > 0) {
    return locale === 'en'
      ? `${hours}h ${minutes}m left`
      : locale === 'ru'
      ? `${hours} ч ${minutes} мин осталось`
      : `${hours} soat ${minutes} daqiqa qoldi`;
  }
  return locale === 'en'
    ? `${minutes}m left`
    : locale === 'ru'
    ? `${minutes} мин осталось`
    : `${minutes} daqiqa qoldi`;
}

export function truncateId(id: string | null | undefined, visibleChars: number = 8): string {
  if (!id) return '';
  if (id.length <= visibleChars + 4) return id;
  return `${id.slice(0, visibleChars)}...${id.slice(-4)}`;
}

export function safeJsonStringify(obj: unknown, indent: number = 2): string {
  try {
    return JSON.stringify(obj, null, indent);
  } catch {
    return String(obj);
  }
}
